import React from "react";

import ActionsDropdown from "../../../../../components/Dropdown/ActionsDropdown";
import { formatCurrency } from "../../../../../utils";
import { rowActionsOf } from "./actions";
import { INTERACTIVE_SELECTOR } from "./constants";
import { rowDateText } from "./format";
import { ProgressAmount } from "./ProgressAmount";
import { ProgressDetailText } from "./ProgressDetailText";
import { ProgressEarnedChip } from "./ProgressEarnedChip";
import { ProgressLock } from "./ProgressLock";
import {
  ActionsCell,
  Cell,
  DateCell,
  DetailText,
  Row,
  Secondary,
  Stack,
  TypeButton,
  Unchanged,
} from "./styles";
import { ProgressTableRowProps } from "./types";

const ProgressTableRowComponent: React.FC<ProgressTableRowProps> = ({
  row,
  actions,
  onOpen,
}) => {
  const dropdownActions = rowActionsOf(row, actions);
  const testId = `progress-entry-actions-${row.transaction?.id ?? row.key}`;
  const menu =
    dropdownActions.length > 0 ? (
      <ActionsDropdown
        compact
        dropdownActions={dropdownActions}
        testId={testId}
      />
    ) : null;
  const balance =
    row.balanceAfter === null ? (
      <Unchanged title="Balance unchanged" aria-label="Balance unchanged">
        —
      </Unchanged>
    ) : (
      formatCurrency(row.balanceAfter)
    );

  const openRow = () => onOpen(row);

  // The menu renders in a portal, so its clicks bubble here through React without being inside the row.
  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
    const target = event.target as HTMLElement;
    const isInsideRow = event.currentTarget.contains(target);
    if (!isInsideRow || target.closest(INTERACTIVE_SELECTOR)) {
      return;
    }
    openRow();
  };

  return (
    <Row onClick={handleRowClick} data-testid={`progress-row-${row.key}`}>
      <DateCell>{rowDateText(row.date)}</DateCell>
      <Cell>
        <Stack>
          <TypeButton type="button" onClick={openRow}>
            {row.type}
          </TypeButton>
          {row.secondary && <Secondary>{row.secondary}</Secondary>}
        </Stack>
      </Cell>
      <Cell>
        <Stack>
          <DetailText>
            <ProgressDetailText row={row} />
          </DetailText>
          {row.sub && <Secondary>{row.sub}</Secondary>}
          {row.cashbackEarned !== null && (
            <ProgressEarnedChip cashbackEarned={row.cashbackEarned} />
          )}
          <ProgressLock row={row} actions={actions} />
        </Stack>
      </Cell>
      <Cell $isNumeric>
        <ProgressAmount row={row} />
      </Cell>
      <Cell $isNumeric>{balance}</Cell>
      <ActionsCell>{menu}</ActionsCell>
    </Row>
  );
};

export const ProgressTableRow = React.memo(ProgressTableRowComponent);
